/**
 * ColorIndicator service provides consistent color schemes for analytics and data visualization.
 * This enhances the user experience by providing visual cues for different data states and values.
 */
class ColorIndicator {
  constructor() {
    // Base color palette
    this.colors = {
      // Primary colors
      primary: '#007BFF',
      secondary: '#6c757d',
      success: '#28a745',
      danger: '#dc3545',
      warning: '#ffc107',
      info: '#17a2b8',
      
      // Extended palette for charts and graphs
      blue: '#007BFF',
      indigo: '#6610f2',
      purple: '#6f42c1',
      pink: '#e83e8c',
      red: '#dc3545',
      orange: '#fd7e14',
      yellow: '#ffc107',
      green: '#28a745',
      teal: '#20c997',
      cyan: '#17a2b8',
      gray: '#6c757d',
      
      // Gradient backgrounds
      gradientSuccess: 'linear-gradient(45deg, #28a745, #20c997)',
      gradientDanger: 'linear-gradient(45deg, #dc3545, #e83e8c)',
      gradientWarning: 'linear-gradient(45deg, #ffc107, #fd7e14)',
      gradientInfo: 'linear-gradient(45deg, #17a2b8, #007BFF)',
    };
    
    // Opacity variants
    this.opacity = {
      light: '33', // 20% opacity (33 in hex)
      medium: '80', // 50% opacity (80 in hex)
      dark: 'CC',   // 80% opacity (CC in hex)
    };
  }
  
  /**
   * Get color for a value based on thresholds
   * @param {number} value - The value to evaluate
   * @param {number} lowThreshold - Threshold for low values (danger)
   * @param {number} mediumThreshold - Threshold for medium values (warning)
   * @param {number} highThreshold - Threshold for high values (success)
   * @param {boolean} inverse - If true, high values are danger and low values are success
   * @returns {string} CSS color value
   */
  getValueColor(value, lowThreshold, mediumThreshold, highThreshold, inverse = false) {
    if (inverse) {
      if (value >= highThreshold) return this.colors.danger;
      if (value >= mediumThreshold) return this.colors.warning;
      if (value >= lowThreshold) return this.colors.success;
      return this.colors.info;
    } else {
      if (value >= highThreshold) return this.colors.success;
      if (value >= mediumThreshold) return this.colors.warning;
      if (value >= lowThreshold) return this.colors.danger;
      return this.colors.info;
    }
  }
  
  /**
   * Get color class for a value based on thresholds
   * @param {number} value - The value to evaluate
   * @param {number} lowThreshold - Threshold for low values (danger)
   * @param {number} mediumThreshold - Threshold for medium values (warning)
   * @param {number} highThreshold - Threshold for high values (success)
   * @param {boolean} inverse - If true, high values are danger and low values are success
   * @returns {string} CSS class name
   */
  getValueColorClass(value, lowThreshold, mediumThreshold, highThreshold, inverse = false) {
    if (inverse) {
      if (value >= highThreshold) return 'text-danger';
      if (value >= mediumThreshold) return 'text-warning';
      if (value >= lowThreshold) return 'text-success';
      return 'text-info';
    } else {
      if (value >= highThreshold) return 'text-success';
      if (value >= mediumThreshold) return 'text-warning';
      if (value >= lowThreshold) return 'text-danger';
      return 'text-info';
    }
  }
  
  /**
   * Get background color class for a value based on thresholds
   * @param {number} value - The value to evaluate
   * @param {number} lowThreshold - Threshold for low values (danger)
   * @param {number} mediumThreshold - Threshold for medium values (warning)
   * @param {number} highThreshold - Threshold for high values (success)
   * @param {boolean} inverse - If true, high values are danger and low values are success
   * @returns {string} CSS class name
   */
  getBackgroundColorClass(value, lowThreshold, mediumThreshold, highThreshold, inverse = false) {
    if (inverse) {
      if (value >= highThreshold) return 'bg-danger';
      if (value >= mediumThreshold) return 'bg-warning';
      if (value >= lowThreshold) return 'bg-success';
      return 'bg-info';
    } else {
      if (value >= highThreshold) return 'bg-success';
      if (value >= mediumThreshold) return 'bg-warning';
      if (value >= lowThreshold) return 'bg-danger';
      return 'bg-info';
    }
  }
  
  /**
   * Get color for percentage values
   * @param {number} percentage - Percentage value (0-100)
   * @param {boolean} inverse - If true, high percentages are danger and low percentages are success
   * @returns {string} CSS color value
   */
  getPercentageColor(percentage, inverse = false) {
    return this.getValueColor(percentage, 30, 60, 80, inverse);
  }
  
  /**
   * Get color class for percentage values
   * @param {number} percentage - Percentage value (0-100)
   * @param {boolean} inverse - If true, high percentages are danger and low percentages are success
   * @returns {string} CSS class name
   */
  getPercentageColorClass(percentage, inverse = false) {
    return this.getValueColorClass(percentage, 30, 60, 80, inverse);
  }
  
  /**
   * Get background color class for percentage values
   * @param {number} percentage - Percentage value (0-100)
   * @param {boolean} inverse - If true, high percentages are danger and low percentages are success
   * @returns {string} CSS class name
   */
  getPercentageBackgroundClass(percentage, inverse = false) {
    return this.getBackgroundColorClass(percentage, 30, 60, 80, inverse);
  }
  
  /**
   * Get color for trend indicators (positive/negative changes)
   * @param {number} value - The change value
   * @param {boolean} inverse - If true, positive changes are danger and negative changes are success
   * @returns {string} CSS color value
   */
  getTrendColor(value, inverse = false) {
    if (value === 0) return this.colors.secondary;
    return value > 0 
      ? (inverse ? this.colors.danger : this.colors.success)
      : (inverse ? this.colors.success : this.colors.danger);
  }
  
  /**
   * Get color class for trend indicators (positive/negative changes)
   * @param {number} value - The change value
   * @param {boolean} inverse - If true, positive changes are danger and negative changes are success
   * @returns {string} CSS class name
   */
  getTrendColorClass(value, inverse = false) {
    if (value === 0) return 'text-secondary';
    return value > 0 
      ? (inverse ? 'text-danger' : 'text-success')
      : (inverse ? 'text-success' : 'text-danger');
  }
  
  /**
   * Get chart colors for datasets
   * @param {number} count - Number of colors needed
   * @returns {Array} Array of color values
   */
  getChartColors(count) {
    const colorKeys = Object.keys(this.colors).filter(key => 
      !key.startsWith('gradient') && 
      key !== 'primary' && 
      key !== 'secondary' && 
      key !== 'success' && 
      key !== 'danger' && 
      key !== 'warning' && 
      key !== 'info'
    );
    
    return Array(count).fill().map((_, i) => 
      this.colors[colorKeys[i % colorKeys.length]]
    );
  }
  
  /**
   * Get a color with specified opacity
   * @param {string} colorName - Name of the color from the palette
   * @param {string} opacityLevel - Opacity level ('light', 'medium', or 'dark')
   * @returns {string} CSS color value with opacity
   */
  getColorWithOpacity(colorName, opacityLevel = 'medium') {
    const color = this.colors[colorName];
    if (!color || color.startsWith('linear-gradient')) return color;
    
    // If it's a hex color, convert to rgba
    if (color.startsWith('#')) {
      const opacity = this.opacity[opacityLevel] || this.opacity.medium;
      return `${color}${opacity}`;
    }
    
    return color;
  }
}

export const colorIndicator = new ColorIndicator();